import { useNavigationDateRange } from "components/scrapEyeDataDashboard/navigation/useNavigationDateRange";
import { datasetSplitsQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { choiceQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { optionalStringListQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";

import { useNavigationQuery } from "components/scrapEyeDataDashboard/navigation/NavigationQueryProvider";
import {
  Alert,
  Autocomplete,
  CircularProgress,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useMemo } from "react";
import dayjs from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useGetModelCoverageConfigQuery } from "src/store/api/generatedApi";
import { MultiSelectAutocomplete } from "components/common/MultiSelectAutocomplete";
import DateTimeControls from "../../components/DateTimeControls";
import { ModelComparisonChart } from "./ModelComparisonChart";
import { useModelComparisonData } from "./hooks/useModelComparisonData";
import {
  type DatasetSplit,
  filterBucketsBySplit,
  splitLabelKey,
} from "../hooks/filterBucketsBySplit";
import type { ComparisonMetric, ModelIdentity } from "./types";

type ModelOption = Readonly<{
  id: string;
  labellingName: string;
  location: string;
}>;

export const ModelComparisonSection = () => {
  const tenant = useTenant();
  const { t } = useTenantTranslation();

  const { pickerValue: dateRange, setPickerValue: setDateRange } =
    useNavigationDateRange("comparisonRange", () => [
      dayjs().subtract(7, "day"),
      dayjs(),
    ]);
  const [selectedModelIds, setSelectedModelIds] = useNavigationQuery<
    readonly string[] | null
  >("comparisonModels", null, optionalStringListQuery, "replace");

  const [selectedMetric, setSelectedMetric] = useNavigationQuery(
    "comparisonMetric",
    "fn_rate",
    choiceQuery([
      "fn_rate",
      "fp_rate",
      "jaccard",
      "mean_event_duration",
    ] as const),
    "replace"
  );
  const [selectedSplits, setSelectedSplits] = useNavigationQuery(
    "comparisonSplits",
    [],
    datasetSplitsQuery,
    "replace"
  );

  const { data: coverageConfig, isLoading: isLoadingConfig } =
    useGetModelCoverageConfigQuery({ tenantName: tenant });

  const modelOptions = useMemo((): readonly ModelOption[] => {
    return Object.entries(coverageConfig?.available_models ?? {}).flatMap(
      ([location, locationModels]) =>
        locationModels.map((model) => ({
          id: `${model.labelling_name}@${location}`,
          labellingName: model.labelling_name,
          location,
        }))
    );
  }, [coverageConfig]);

  const selectedModels = useMemo((): readonly ModelIdentity[] => {
    const optionMap = new Map(modelOptions.map((m) => [m.id, m]));
    return (selectedModelIds ?? modelOptions.map((model) => model.id))
      .map((id) => optionMap.get(id))
      .filter((m): m is ModelOption => m !== undefined)
      .map((m) => ({ labellingName: m.labellingName, location: m.location }));
  }, [selectedModelIds, modelOptions]);

  const { bucketGroups, failedModels, isLoading, splitPeriods } =
    useModelComparisonData(selectedModels, dateRange[0], dateRange[1]);

  const filteredBucketGroups = useMemo(
    () =>
      bucketGroups
        .map((group) => ({
          ...group,
          buckets: filterBucketsBySplit(
            group.buckets,
            splitPeriods,
            selectedSplits
          ),
        }))
        .filter((group) => group.buckets.length > 0),
    [bucketGroups, splitPeriods, selectedSplits]
  );

  return (
    <Stack
      spacing={2}
      sx={{
        p: 3,
        backgroundColor: "grey.50",
        borderRadius: 2,
        border: 1,
        borderColor: "grey.300",
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="h5" fontWeight={600} color="text.primary">
          {t("modelComparisonTitle")}
        </Typography>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={selectedMetric}
          onChange={(_, value: ComparisonMetric | null) => {
            if (value) setSelectedMetric(value);
          }}
        >
          <ToggleButton value="fn_rate">{t("falseNegativeRate")}</ToggleButton>
          <ToggleButton value="fp_rate">{t("falsePositiveRate")}</ToggleButton>
          <ToggleButton value="jaccard">{t("jaccardAccuracy")}</ToggleButton>
          <ToggleButton value="mean_event_duration">
            {t("meanEventDuration")}
          </ToggleButton>
        </ToggleButtonGroup>
        <Autocomplete
          multiple
          size="small"
          limitTags={2}
          disableCloseOnSelect
          disabled={splitPeriods.length === 0}
          options={["train", "val", "test"] as DatasetSplit[]}
          getOptionLabel={(option) => t(splitLabelKey(option))}
          value={selectedSplits}
          onChange={(_, value) => {
            setSelectedSplits(value);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={
                selectedSplits.length === 0 ? t("datasetSplitAll") : undefined
              }
              sx={{ minWidth: 200 }}
            />
          )}
        />
      </Stack>

      <DateTimeControls pickerValue={dateRange} onRangeChange={setDateRange} />

      <MultiSelectAutocomplete
        options={modelOptions}
        selectedIds={selectedModelIds ?? modelOptions.map((model) => model.id)}
        onChange={(ids) => {
          setSelectedModelIds(ids);
          setSelectedSplits([]);
        }}
        getOptionLabel={(option) =>
          `${option.labellingName} (${option.location})`
        }
        filterOption={(option, input) => {
          const query = input.toLowerCase();
          return (
            option.labellingName.toLowerCase().includes(query) ||
            option.location.toLowerCase().includes(query)
          );
        }}
        label={t("modelComparisonSelectModels")}
      />

      {isLoadingConfig || isLoading ? (
        <Stack alignItems="center" py={4}>
          <CircularProgress size={24} />
        </Stack>
      ) : filteredBucketGroups.length === 0 ? (
        <Alert severity="info">{t("modelComparisonNoData")}</Alert>
      ) : (
        <>
          {failedModels.length > 0 ? (
            <Alert severity="warning">
              {t("modelComparisonLoadError", {
                models: failedModels.map((m) => m.labellingName).join(", "),
              })}
            </Alert>
          ) : null}
          <ModelComparisonChart
            bucketGroups={filteredBucketGroups}
            selectedMetric={selectedMetric}
          />
        </>
      )}
    </Stack>
  );
};
